/**
 * AppRuntimeProvider — infrastructure-only runtime container.
 *
 * Holds: QueryClient, AppTransport, ModuleRegistry, ModuleStoreRegistry, AgentRuntime.
 * Does NOT hold module-specific business state.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { AppRuntime } from "../runtime/contracts";
import type { AppTransport } from "../runtime/contracts";
import type { ModuleDefinition, ModuleRegistry, ModuleStoreRegistry } from "../runtime/contracts";
import type { AgentRuntime, AgentRuntimeState } from "../runtime/contracts";
import type { StoreApi } from "zustand/vanilla";
import { createStore } from "zustand/vanilla";
import { AgentContributionRegistry } from "../runtime/agent/contributions";
import { AgentChatStore } from "../runtime/agent/AgentChatStore";
import { useWebSocket } from "../hooks/useWebSocket";
import type { StreamEvent } from "./WebSocketProvider";
import { useWorkspaceStore } from "../store/workspaceStore";
import type {
  ComposerRuntimeSurface,
  ComposerPresenceParams,
  ComposerApplyEventPayload,
} from "../runtime/contracts";
import { ComposerMountProvider } from "../components/shared/composer/ComposerMountContext";

// ─── Runtime Context ──────────────────────────────────────────────────

const AppRuntimeContext = createContext<AppRuntime | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAppRuntime(): AppRuntime {
  const ctx = useContext(AppRuntimeContext);
  if (!ctx) throw new Error("useAppRuntime must be used inside AppRuntimeProvider");
  return ctx;
}

// ─── Transport Adapter ────────────────────────────────────────────────

interface WsShape {
  rpc: { rpc: <T>(method: string, params?: Record<string, unknown>) => Promise<T>; rpcStream: (method: string, params: Record<string, unknown>, onChunk: (d: unknown) => void) => Promise<unknown>; baseUrl: string };
  onSchemaEvent: (ids: readonly string[], h: (e: StreamEvent) => void) => () => void;
  onEventType: (types: readonly string[], h: (e: StreamEvent) => void) => () => void;
}

/**
 * Creates a transport that always delegates to the latest ws ref.
 * This ensures subscriptions created by setup() survive ws reconnections.
 */
function createProxyTransport(wsRef: { current: WsShape }): AppTransport {
  return {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get baseUrl() { return wsRef.current.rpc.baseUrl; },
    rpc: <T,>(method: string, params?: Record<string, unknown>) => wsRef.current.rpc.rpc<T>(method, params),
    rpcStream: (method, params, onChunk) => wsRef.current.rpc.rpcStream(method, params, onChunk),
    onSchemaEvent: (ids, handler) => wsRef.current.onSchemaEvent(ids, handler as (e: StreamEvent) => void),
    onEventType: (types, handler) => wsRef.current.onEventType(types, handler as (e: StreamEvent) => void),
  };
}

// ─── Module Registry ──────────────────────────────────────────────────

function createModuleRegistry(): ModuleRegistry {
  const modules = new Map<string, ModuleDefinition>();
  const ordered: ModuleDefinition[] = [];

  return {
    list: () => ordered,
    get: (id) => modules.get(id),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    register: (def) => {
      if (!modules.has(def.id)) {
        modules.set(def.id, def);
        ordered.push(def);
      }
    },
  };
}

// ─── Module Store Registry ────────────────────────────────────────────

function createModuleStoreRegistry(): ModuleStoreRegistry {
  const stores = new Map<string, StoreApi<unknown>>();

  return {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    register: (moduleId, store) => { stores.set(moduleId, store); },
    get: (moduleId) => stores.get(moduleId) as never,
    has: (moduleId) => stores.has(moduleId),
  };
}

// ─── Agent Runtime ────────────────────────────────────────────────────

// ─── Agent Runtime ────────────────────────────────────────────────────

function createAgentRuntime(
  transport: AppTransport,
  runtimeRef: { current: AppRuntime | null },
): AgentRuntime {
  const registry = new AgentContributionRegistry();
  const chatStore = new AgentChatStore();

  const store = createStore<AgentRuntimeState>(() => ({
    activeContext: null,
    replyTo: null,
    isStreaming: false,
    pendingApprovals: [],
  }));

  const chat = {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    subscribe: (k: string, l: (state: unknown) => void) => chatStore.subscribe(k, l),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getState: (k: string) => chatStore.getState(k),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    loadLatestEpisode: (k: string, entityId?: string) => chatStore.loadLatestEpisode(transport, k, entityId),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    loadEpisodeById: (k: string, episodeId: string) => chatStore.loadEpisodeById(transport, k, episodeId),
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports, @typescript-eslint/explicit-function-return-type
    sendMessage: (k: string, text: string, ctx?: unknown, eid?: string, ceid?: string, attachments?: readonly import("../modules/episodes/types").ChatMessageAttachment[], displayContent?: string, episodeTitle?: string, systemPrompt?: string, engine?: string, model?: string) =>
      chatStore.sendMessage(transport, k, text, ctx as never, eid, ceid, attachments, displayContent, episodeTitle, systemPrompt, engine, model),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    approveToolCall: (k: string, tid: string, approved: boolean, argumentsOverride?: unknown) =>
      chatStore.approveToolCall(transport, k, tid, approved, argumentsOverride),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    markToolCallDone: (k: string, tid: string) =>
      { chatStore.markToolCallDone(transport, k, tid); },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    startNewEpisode: (k: string, presetId?: string) => chatStore.startNewEpisode(transport, k, presetId),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stopStream: (k: string) => { chatStore.stopStream(k); },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onUsage: (l: (event: unknown) => void) => chatStore.onUsage(l),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onTodo: (l: (snapshot: unknown) => void) => chatStore.onTodo(l),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getTodo: (k: string) => chatStore.getTodo(k),
  };

  return {
    store,
    chat,
    registerContribution: (moduleId, contribution) => registry.register(moduleId, contribution),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setActiveContext: (context) => {
      store.setState({ activeContext: context });
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setReplyTo: (replyTo) => {
      store.setState({ replyTo });
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    send: async (input) => {
      await chat.sendMessage(input.contextKey, input.text, input.context);
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    approveToolCall: async (contextKey, toolCallId, argumentsOverride) => {
      await chat.approveToolCall(contextKey, toolCallId, true, argumentsOverride);
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    denyToolCall: async (contextKey, toolCallId) => {
      await chat.approveToolCall(contextKey, toolCallId, false);
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    requestDraft: (request) => {
      if (runtimeRef.current) {
        registry.handleDraftRequest(request.targetModuleId, request.payload, runtimeRef.current);
      }
    },
    resolveEntityRenderer: (schemaId) => registry.resolveEntityRenderer(schemaId),
    navigateToEntity: (schemaId, entityId, data, rt, nav) =>
      registry.navigateToEntity(schemaId, entityId, data, rt, nav),
    resolveHistoryRenderer: (block) => registry.resolveHistoryRenderer(block),
    resolveTodoRenderer: (item) => registry.resolveTodoRenderer(item),
    resolveAllowlistTarget: (tc) => registry.resolveAllowlistTarget(tc),
    resolveSystemPrompt: (moduleId) => registry.resolveSystemPrompt(moduleId),
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    dispatchContextAction: (moduleId, actionId, payload) => {
      const actions = registry.getContextActions(moduleId);
      const action = actions.find((a) => a.id === actionId);
      if (action) {
        const context = store.getState().activeContext ?? { moduleId };
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        void action.run({ runtime: runtimeRef.current!, context, payload });
      }
    },
  };
}

// ─── Provider ─────────────────────────────────────────────────────────

export function AppRuntimeProvider({ children, moduleDefinitions }: { readonly children: ReactNode; readonly moduleDefinitions?: readonly ModuleDefinition[] }): JSX.Element {
  const ws = useWebSocket();
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);

  const queryClientRef = useRef<QueryClient | null>(null);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  if (queryClientRef.current === null) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          retry: 1,
        },
      },
    });
  }
  const queryClient = queryClientRef.current;

  // Mutable ref to latest ws — transport proxy delegates through this
  const wsRef = useRef(ws);
  wsRef.current = ws;

  const runtimeRef = useRef<AppRuntime | null>(null);
  if (runtimeRef.current === null) {
    const transport = createProxyTransport(wsRef);
    const modules = createModuleRegistry();
    const stores = createModuleStoreRegistry();
    const agent = createAgentRuntime(transport, runtimeRef);

    // Stage 4: fan-out from the single WS `composer.apply` subscription to
    // multiple `onApply` subscribers. The AppTransport bus filter already
    // scopes delivery per-user (INV-15); this layer just distributes within
    // the app.
    const applySubscribers = new Set<(e: ComposerApplyEventPayload) => void>();

    // Serialises `setPresence` RPCs so rapid mount/unmount/remount
    // sequences (happen during route transitions) cannot be reordered
    // by the concurrent RPC dispatcher on the backend.
    let presenceChain: Promise<unknown> = Promise.resolve();

    const composer: ComposerRuntimeSurface = {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      setPresence: (params: ComposerPresenceParams | null) => {
        // Serialize setPresence calls so a rapid mount/unmount/remount
        // sequence cannot land out-of-order on the backend (which would
        // leave presence in the wrong state). The backend RPCs are
        // dispatched on parallel tasks and have no per-user ordering
        // guarantee, so we must enforce order at the source.
        // A rejection here MUST surface — swallowing it silently hides a
        // broken presence contract and leaves write handlers returning
        // `composer_unavailable` with no user-visible signal. We log and
        // rethrow into the chain, then reset the chain so the next call
        // isn't starved behind the rejected one.
        presenceChain = presenceChain
          .catch(() => { /* prior failure already logged below */ })
          .then(() => transport.rpc("runtime.composer.setPresence", { presence: params }))
          .catch((err: unknown) => {
            // eslint-disable-next-line no-console
            console.error("runtime.composer.setPresence failed", err);
            throw err;
          });
      },
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onApply: (handler: (event: ComposerApplyEventPayload) => void) => {
        applySubscribers.add(handler);
        return (): void => {
          applySubscribers.delete(handler);
        };
      },
    };

    // Subscribe once to the WS event bus for `composer.apply`. The backend
    // event bus carries envelopes of the form
    //   { type:"composer.apply", user_id, payload:{mode,thread_key,op,...} }
    // and the WS forwarder re-wraps them into
    //   { type:"event", event_type:"composer.apply", payload:<envelope> }
    // After `onEventType` unwraps one layer the inner `.payload` still
    // holds the original envelope — we dereference `.payload.payload` to
    // reach the composer fields.
    transport.onEventType(["composer.apply"], (event) => {
      const envelope = (event as { readonly payload?: unknown }).payload;
      if (!envelope || typeof envelope !== "object") return;
      const inner = (envelope as { readonly payload?: unknown }).payload;
      if (!inner || typeof inner !== "object") return;
      const p = inner as Record<string, unknown>;
      const mode = p.mode;
      if (mode !== "email" && mode !== "telegram") return;
      if (typeof p.thread_key !== "string") return;
      if (typeof p.revision !== "number") return;
      if (typeof p.op !== "string") return;
      const candidate = inner as ComposerApplyEventPayload;
      for (const fn of applySubscribers) fn(candidate);
    });

    const runtime: AppRuntime = {
      queryClient,
      transport,
      modules,
      stores,
      agent,
      composer,
    };

    // Bootstrap: register all modules, create stores, register agent contributions
    if (moduleDefinitions) {
      for (const mod of moduleDefinitions) {
        modules.register(mod);
      }
    }
    for (const mod of modules.list()) {
      if (mod.createStore) {
        const store = mod.createStore(runtime);
        stores.register(mod.id, store);
      }
      if (mod.agent) {
        agent.registerContribution(mod.id, mod.agent);
      }
    }

    runtimeRef.current = runtime;
  }

  // Run module setup() hooks (query invalidation subscriptions, etc.)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const runtime = runtimeRef.current!;
    const cleanups: (() => void)[] = [];
    for (const mod of runtime.modules.list()) {
      if (mod.setup) {
        const store = runtime.stores.get(mod.id);
        const result = mod.setup(runtime, store as never);
        if (typeof result === "function") {
          cleanups.push(result);
        } else if (result instanceof Promise) {
          void result.then((fn) => { if (typeof fn === "function") cleanups.push(fn); });
        }
      }
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { for (const fn of cleanups) fn(); };
  }, []);

  const runtime = runtimeRef.current;
  const previousWorkspaceIdRef = useRef<string | null>(selectedWorkspaceId);

  useEffect(() => {
    if (
      previousWorkspaceIdRef.current !== null &&
      selectedWorkspaceId !== null &&
      previousWorkspaceIdRef.current !== selectedWorkspaceId
    ) {
      queryClient.clear();
    }
    previousWorkspaceIdRef.current = selectedWorkspaceId;
  }, [queryClient, selectedWorkspaceId]);

  return (
    <AppRuntimeContext.Provider value={runtime}>
      <QueryClientProvider client={queryClient}>
        <ComposerMountProvider>{children}</ComposerMountProvider>
      </QueryClientProvider>
    </AppRuntimeContext.Provider>
  );
}
